import React, { useEffect, useState } from 'react';
import { Alert, App, Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Spin } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { useProjectsForSelection } from '@/hooks/useProject';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { recruitErrorMessage } from './shared/recruitUi';

const EMPLOYMENT_TYPES = ['Permanent', 'Full-time', 'Part-time', 'Contract', 'Temporary', 'Intern'];
const HIRING_TYPES = ['New headcount', 'Replacement', 'Backfill', 'Project / temporary'];
const URGENCY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];
const CURRENCIES = ['AED', 'SAR', 'USD', 'EUR', 'INR', 'GBP'];
/** After approval only ownership / logistics may change; headcount and budget are locked. */
const LOCKED_AFTER_SUBMIT = new Set(['Open', 'OnHold', 'PendingApproval']);

const RequisitionFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { data: departments = [] } = useDepartmentsForSelection();
  const { data: projects = [] } = useProjectsForSelection();
  const { data: positions = [] } = useQuery({ queryKey: hrmsKeys.positions(), queryFn: () => hrmsService.listPositions() });
  const { data: grades = [] } = useQuery({ queryKey: hrmsKeys.grades(), queryFn: () => hrmsService.listGrades() });
  const { data: existing, isLoading } = useQuery({
    queryKey: hrmsKeys.requisition(id || ''),
    queryFn: () => hrmsService.getRequisition(id!),
    enabled: isEdit,
  });

  const status: string = existing?.status || 'Draft';
  const limited = isEdit && LOCKED_AFTER_SUBMIT.has(status);
  const readOnly = isEdit && !['Draft', 'Rejected'].includes(status) && !limited;

  useEffect(() => {
    if (existing) {
      form.setFieldsValue({
        ...existing,
        target_join_date: existing.target_join_date ? dayjs(existing.target_join_date) : undefined,
        budget_min: existing.budget_min != null ? Number(existing.budget_min) : undefined,
        budget_max: existing.budget_max != null ? Number(existing.budget_max) : undefined,
      });
    }
  }, [existing, form]);

  const save = async () => {
    try {
      const v = await form.validateFields();
      const body = { ...v, target_join_date: v.target_join_date ? v.target_join_date.format('YYYY-MM-DD') : null };
      setSaving(true);
      const saved = isEdit ? await hrmsService.updateRequisition(id!, body) : await hrmsService.createRequisition(body);
      message.success(isEdit ? 'Requisition updated' : 'Draft requisition created');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.requisitions() });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.requisition(saved.id) });
      navigate(`/hr/requisitions/${saved.id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(recruitErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Requisition" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }

  const lockedField = limited || readOnly;

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isEdit ? `Edit requisition ${existing?.requisition_number || ''}` : 'New manpower requisition'}
          subtitle="Headcount request — save as draft, then submit for approval"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(isEdit ? `/hr/requisitions/${id}` : '/hr/requisitions')}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={readOnly} onClick={save}>
                {isEdit ? 'Save changes' : 'Create draft'}
              </Button>
            </Space>
          }
        />
      }
    >
      {limited && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16, maxWidth: 960 }}
          message={`This requisition is ${status}. Headcount, budget and role details are locked; you can still change the recruiter, hiring manager, target date, location and notes.`}
        />
      )}
      {readOnly && (
        <Alert type="warning" showIcon style={{ marginBottom: 16, maxWidth: 960 }} message={`A ${status} requisition cannot be edited.`} />
      )}
      <Form
        form={form}
        layout="vertical"
        disabled={readOnly}
        initialValues={{
          headcount: 1,
          employment_type: 'Permanent',
          hiring_type: 'New headcount',
          urgency: 'Medium',
          min_experience_years: 0,
          currency: 'AED',
        }}
        style={{ maxWidth: 960 }}
      >
        <Card title="Role" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="job_title" label="Job title" rules={[{ required: true, message: 'Title is required' }]}>
                <Input placeholder="e.g. Senior Electrician" disabled={lockedField} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="department_id" label="Department" rules={[{ required: true, message: 'Department is required' }]}>
                <Select
                  showSearch
                  disabled={lockedField}
                  placeholder="Select department"
                  optionFilterProp="label"
                  options={departments.map((d: any) => ({ value: d.id, label: d.department_name || d.name || d.code }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="position_id" label="Position">
                <Select
                  allowClear
                  showSearch
                  disabled={lockedField}
                  placeholder="Link HR position"
                  optionFilterProp="label"
                  options={positions.map((p: any) => ({ value: p.id, label: p.position_name || p.name || p.position_code }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="grade_id" label="Grade">
                <Select
                  allowClear
                  showSearch
                  disabled={lockedField}
                  placeholder="Link grade band"
                  optionFilterProp="label"
                  options={grades.map((g: any) => ({ value: g.id, label: g.grade_name || g.name || g.grade_code }))}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="headcount" label="Openings" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} disabled={lockedField} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="urgency" label="Urgency">
                <Select disabled={lockedField} options={URGENCY_OPTIONS.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="employment_type" label="Employment type">
                <Select disabled={lockedField} options={EMPLOYMENT_TYPES.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="hiring_type" label="Hiring type">
                <Select disabled={lockedField} options={HIRING_TYPES.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="location_text" label="Location">
                <Input placeholder="Site / city / branch" disabled={readOnly} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="min_experience_years" label="Minimum experience (years)">
                <InputNumber min={0} style={{ width: '100%' }} disabled={lockedField} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="required_skills" label="Required skills">
                <Input.TextArea rows={2} placeholder="Welding, Scaffolding, ..." disabled={lockedField} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Budget & timing" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="currency" label="Currency">
                <Select disabled={lockedField} options={CURRENCIES.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="budget_min" label="Monthly budget — min">
                <InputNumber min={0} style={{ width: '100%' }} disabled={lockedField} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="budget_max"
                label="Monthly budget — max"
                dependencies={['budget_min']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: (_, v) =>
                      v == null || getFieldValue('budget_min') == null || v >= getFieldValue('budget_min')
                        ? Promise.resolve()
                        : Promise.reject(new Error('Max must be at least the minimum')),
                  }),
                ]}
              >
                <InputNumber min={0} style={{ width: '100%' }} disabled={lockedField} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="target_join_date" label="Target join date">
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" disabled={readOnly} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="cost_center_code" label="Cost centre">
                <Input placeholder="Cost centre code" disabled={lockedField} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="project_id" label="Project (if project hire)">
                <Select
                  allowClear
                  showSearch
                  disabled={lockedField}
                  optionFilterProp="label"
                  placeholder="Select project"
                  options={(projects as any[]).map((p: any) => ({
                    value: p.id,
                    label: [p.project_number || p.project_code, p.project_name || p.name].filter(Boolean).join(' — '),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Ownership & justification">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="recruiter_employee_id" label="Recruiter" extra="Owns sourcing and screening for this role">
                <RecruitPeoplePicker disabled={readOnly} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="hiring_manager_employee_id" label="Hiring manager">
                <RecruitPeoplePicker disabled={readOnly} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="justification" label="Justification">
                <Input.TextArea rows={3} placeholder="Business need / headcount rationale" disabled={lockedField} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} disabled={readOnly} />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default RequisitionFormPage;
